import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../lib/api'
import { useAuth } from '../context/AuthContext'
import { useTenant } from '../context/TenantContext'
import { queryKeys } from '../lib/queryKeys'
import { invalidateAfterInfluencerInvitationChange } from '../lib/invalidateSharedQueries'
import { SHARED_QUERY_REFETCH_MS } from '../lib/queryPolling'

/**
 * Custom hook for managing influencer invitations and assignments (TanStack Query)
 * @param {string|null} [campaignId=null] - Filter by campaign id (null = all).
 * @param {{ enabled?: boolean, clientId?: string|null }} [options] - Optional. enabled: false to skip query. clientId: when no campaignId, filter to influencers in at least one campaign for that client.
 */
export const useInfluencers = (campaignId = null, options = {}) => {
  const { userProfile, apiTenantSlug } = useAuth()
  const { tenant } = useTenant()
  const queryClient = useQueryClient()
  const enabled = !!userProfile?.id && (options.enabled !== false)
  const clientId = options.clientId ?? null
  const accountStatus = options.accountStatus ?? 'active'
  const tenantCacheKey = apiTenantSlug || tenant?.slug || ''
  const poll = options.poll === true
  const isInfluencerUser = userProfile?.role === 'influencer'

  const {
    data: influencers = [],
    isLoading: loading,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.influencers.list(campaignId, clientId, tenantCacheKey, accountStatus),
    queryFn: ({ signal }) =>
      api.influencers.getAll(campaignId, {
        signal,
        ...(clientId && { clientId }),
        ...(accountStatus && { accountStatus }),
      }),
    enabled,
    refetchOnWindowFocus: userProfile?.role === 'admin',
    refetchInterval: poll || (isInfluencerUser && !campaignId) ? SHARED_QUERY_REFETCH_MS : false,
    refetchIntervalInBackground: false,
  })

  const inviteMutation = useMutation({
    mutationFn: ({ inviteCampaignId, influencerId }) =>
      api.influencers.invite(inviteCampaignId, influencerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.influencers.all })
    },
  })

  const updateStatusMutation = useMutation({
    mutationFn: ({ invitationId, status }) => api.influencers.updateStatus(invitationId, status),
    onSuccess: (_data, { status }) => {
      if ((status || '').toLowerCase() === 'accepted') {
        invalidateAfterInfluencerInvitationChange(queryClient)
      } else {
        queryClient.invalidateQueries({ queryKey: queryKeys.influencers.all })
      }
    },
  })

  const removeMutation = useMutation({
    mutationFn: (invitationId) => api.influencers.remove(invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.influencers.all })
    },
  })

  const updateAssignmentMutation = useMutation({
    mutationFn: ({ assignmentId, updates }) => api.influencers.update(assignmentId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.influencers.all })
    },
  })

  const fetchCampaignInfluencers = refetch
  const fetchInfluencers = refetch

  const inviteInfluencer = async (inviteCampaignId, influencerId) => {
    if (!userProfile || (userProfile.role !== 'admin' && userProfile.role !== 'client')) {
      return { data: null, error: new Error('Only admins and clients can invite influencers') }
    }
    try {
      const data = await inviteMutation.mutateAsync({ inviteCampaignId, influencerId })
      return { data, error: null }
    } catch (err) {
      console.error('Error inviting influencer:', err)
      return { data: null, error: err }
    }
  }

  const updateInfluencerStatus = async (invitationId, status) => {
    if (!userProfile) return { error: new Error('Not authenticated') }
    try {
      await updateStatusMutation.mutateAsync({ invitationId, status })
      return { error: null }
    } catch (err) {
      console.error('Error updating influencer status:', err)
      return { error: err }
    }
  }

  const removeInfluencer = async (invitationId) => {
    if (!userProfile || (userProfile.role !== 'admin' && userProfile.role !== 'client')) {
      return { error: new Error('Only admins and clients can remove influencers') }
    }
    try {
      await removeMutation.mutateAsync(invitationId)
      return { error: null }
    } catch (err) {
      console.error('Error removing influencer:', err)
      return { error: err }
    }
  }

  const updateAssignment = async (assignmentId, updates, options = {}) => {
    if (!userProfile || (userProfile.role !== 'admin' && userProfile.role !== 'client')) {
      return { error: new Error('Only admins and clients can update assignments') }
    }
    try {
      const result = await updateAssignmentMutation.mutateAsync({
        assignmentId,
        updates,
        campaignId: options.campaignId ?? campaignId,
      })
      return { data: result, error: null }
    } catch (err) {
      console.error('Error updating assignment:', err)
      return { data: null, error: err }
    }
  }

  return {
    influencers,
    loading,
    error: error?.message ?? null,
    fetchCampaignInfluencers,
    fetchInfluencers,
    inviteInfluencer,
    updateInfluencerStatus,
    removeInfluencer,
    updateAssignment,
  }
}
